/**
 * Approval events on a session's timeline (R-HL-1, R-HL-3): tool requests a person allowed, the
 * decisions that stopped it (expired, denied, refused by policy) and what happened after
 * (approved and resumed, the call ran once, marked as handled). Newest first, at most 5, as quiet
 * rows: Approve and resume is on the blocker card above, never twice. A request asking right
 * now is answered on its card above (SessionApprovals), so it shows once, and joins the timeline
 * once answered. Renders nothing when the session has none.
 */
import type { ComponentType } from 'react';
import { Check, CircleCheck, CornerUpLeft, MessageCircleQuestion, Play, ShieldAlert, ShieldCheck, ShieldX, TriangleAlert } from '@orgus/ui/icons';
import { formatClock, cn } from '@orgus/ui';
import type { Session } from '@orgus/protocol';
import { useApprovals } from './live.ts';
import { ApproveAndResumeButton } from './ApproveAndResumeButton.tsx';
import { approvalEvents, askEvents, useApprovalBlock } from './blocked.ts';
import { useWho } from '../asks/AskActions.tsx';
import type { ApprovalEventKind, ApprovalEventRow } from './blocked.ts';

const EVENT: Record<ApprovalEventKind, { icon: ComponentType<{ className?: string }>; cls: string }> = {
  allowed: { icon: ShieldCheck, cls: 'text-passed' },
  expired: { icon: ShieldAlert, cls: 'text-failed' },
  denied: { icon: ShieldX, cls: 'text-failed' },
  policy: { icon: ShieldX, cls: 'text-failed' },
  resume_failed: { icon: TriangleAlert, cls: 'text-failed' },
  resumed: { icon: Play, cls: 'text-working' },
  approved: { icon: ShieldCheck, cls: 'text-waiting' },
  ran: { icon: CircleCheck, cls: 'text-passed' },
  handled: { icon: Check, cls: 'text-fg-subtle' },
  ask_open: { icon: MessageCircleQuestion, cls: 'text-fg-subtle' },
  ask_answered: { icon: CircleCheck, cls: 'text-passed' },
  ask_declined: { icon: CornerUpLeft, cls: 'text-fg-subtle' },
};

/** The rows, without store reads: rendered in tests. `resumable` puts the button on the active row. */
export function ApprovalEventList({ rows, resumable, clock = formatClock }: { rows: ApprovalEventRow[]; resumable: boolean; clock?: (ts: number) => string }) {
  if (!rows.length) return null;
  return (
    // Quiet rows like the transcript's: the state is the small icon, never a tinted block.
    <ol aria-label="Approval events" className="flex flex-col overflow-hidden rounded-card border border-border-subtle bg-raised py-1">
      {rows.map((r) => {
        const e = EVENT[r.kind];
        const I = e.icon;
        return (
          <li key={r.key} className="flex flex-col gap-2 px-3 py-2">
            <div className="flex items-center gap-2">
              <I className={cn('size-3.5 shrink-0', e.cls)} aria-hidden />
              <span className="min-w-0 flex-1 truncate text-sm text-fg">{r.label}</span>
              <time dateTime={new Date(r.at).toISOString()} className="shrink-0 text-sm text-fg-subtle num">
                {clock(r.at)}
              </time>
            </div>
            {r.command && <code className="line-clamp-3 block pl-5.5 font-mono text-sm break-all whitespace-pre-wrap text-fg-muted">{r.command}</code>}
            {r.detail && !r.answers && <p className="line-clamp-2 pl-5.5 text-sm text-fg-muted">{r.detail}</p>}
            {r.answers && (
              <dl className="flex flex-col gap-1 pl-5.5 text-sm" aria-label="Answers">
                {r.answers.map((x) => (
                  <div key={x.question} className="flex min-w-0 gap-2">
                    <dt className="w-24 shrink-0 truncate text-fg-subtle" title={x.question}>
                      {x.header || x.question}
                    </dt>
                    <dd className="min-w-0 wrap-anywhere text-fg">{x.answer}</dd>
                  </div>
                ))}
              </dl>
            )}
            {r.active && resumable && (
              <div className="pl-5.5">
                <ApproveAndResumeButton approvalId={r.approvalId} variant="secondary" />
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * The questions and plans some sessions asked and how each was settled: answered in Orgus, or
 * passed back and asked or answered in the session (ADR 0056), with each question's answer.
 * Mount: the mission page. Renders nothing when there are none.
 */
export function AskHistory({ sessionIds, label = 'Questions' }: { sessionIds: readonly string[]; label?: string }) {
  const { approvals } = useApprovals();
  const who = useWho();
  const rows = askEvents(sessionIds, approvals, who);
  if (!rows.length) return null;
  return (
    <section aria-label={label}>
      <ApprovalEventList rows={rows} resumable={false} />
    </section>
  );
}

/** How many decided questions and plans some sessions have (the mission panel's count). */
export function useAskCount(sessionIds: readonly string[]): number {
  const { approvals } = useApprovals();
  return askEvents(sessionIds, approvals, (by) => by).length;
}

/** Mount: SessionDrawer's Timeline section, above the transcript. */
export function ApprovalEvents({ session }: { session: Session }) {
  const { approvals } = useApprovals();
  const block = useApprovalBlock(session);
  const who = useWho();
  // The decision that blocks it right now is the blocker card above (ApprovalBlocker, mounted
  // beside this list wherever it is): what was asked, when it expired, Approve and resume. Its
  // timeline row would say it a second time, so it joins the timeline once the block is settled.
  const rows = approvalEvents(session.id, approvals, block?.approval.id ?? null, 5, who).filter((r) => !r.active);
  return <ApprovalEventList rows={rows} resumable={false} />;
}
